import { useQuery } from "@tanstack/react-query";
import { addMonths, format, subMonths } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import type {
  CapacityRunwayView,
  CounterfactualPoint,
  CounterfactualView,
  ProductivityProjection,
  RunwayScenario,
} from "./types";

const PRE_MONTHS = 14;
const POST_MONTHS = 10;
const PROJECTION_MONTHS = 18;

function buildCounterfactual(officeIsLive: boolean): CounterfactualView {
  const start = subMonths(new Date(), PRE_MONTHS + POST_MONTHS - 1);

  const points: CounterfactualPoint[] = Array.from(
    { length: PRE_MONTHS + POST_MONTHS },
    (_, i) => {
      const period = format(addMonths(start, i), "yyyy-MM");
      // Decision times were already drifting down slowly before go-live.
      const baseline = Number((15.2 - 0.11 * i + Math.sin(i * 1.4) * 0.55).toFixed(1));
      const post = i - PRE_MONTHS;

      if (!officeIsLive || post < 0) {
        return { period, actualHours: null, extrapolatedBaselineHours: baseline };
      }

      const t = post / (POST_MONTHS - 1);
      const actual = Number((baseline - (0.6 + 2.6 * t) + Math.sin(post * 1.9) * 0.3).toFixed(1));
      return { period, actualHours: actual, extrapolatedBaselineHours: baseline };
    },
  );

  const last = points[points.length - 1]!;
  const gap =
    last.actualHours == null
      ? 0
      : Number((last.extrapolatedBaselineHours - last.actualHours).toFixed(1));

  return {
    metric: "first_decision_time",
    fitQuality: "noisy_use_caution",
    fitNote:
      "Pre-Athena data covers roughly 14 months with real month-to-month variance — read this trend as directional, not precise.",
    points,
    latestAttributableGapHours: gap,
  };
}

const SCENARIO_SHAPE: Record<
  RunwayScenario["name"],
  { label: string; growthPerMonth: number; damping: number }
> = {
  current_trend_continues: { label: "Current trend continues", growthPerMonth: 2.9, damping: 1 },
  efficiency_plateaus: { label: "Efficiency plateaus", growthPerMonth: 2.9, damping: 0.12 },
  volume_grows_20pct: { label: "Volume grows 20%", growthPerMonth: 4.6, damping: 1 },
};

function buildRunway(officeIsLive: boolean): CapacityRunwayView {
  const ceilingCapacityRatio = officeIsLive ? 118 : 92;
  const startRatio = officeIsLive ? 96 : 84;
  const start = new Date();

  const scenarios = (Object.keys(SCENARIO_SHAPE) as RunwayScenario["name"][]).map((name) => {
    const shape = SCENARIO_SHAPE[name];
    const projectedCapacityRatio = Array.from({ length: PROJECTION_MONTHS }, (_, i) => {
      // Damped growth: plateau scenarios flatten quickly, trend scenarios keep climbing.
      const damped =
        shape.damping >= 1
          ? shape.growthPerMonth * i
          : (shape.growthPerMonth / shape.damping) * (1 - Math.exp(-shape.damping * i));
      return {
        period: format(addMonths(start, i), "yyyy-MM"),
        value: Number((startRatio + damped).toFixed(1)),
      };
    });

    return { name, label: shape.label, projectedCapacityRatio } satisfies RunwayScenario;
  });

  const headroomMonthsEstimate = scenarios.map((scenario) => {
    const hit = scenario.projectedCapacityRatio.findIndex((p) => p.value >= ceilingCapacityRatio);
    return { scenario: scenario.name, monthsUntilCeiling: hit === -1 ? null : hit };
  });

  return {
    ceilingCapacityRatio,
    ceilingBasisNote:
      "Based on the highest capacity ratio sustained for a full month since go-live, not the average — a level the team has already cleared once, not a hopeful extrapolation.",
    scenarios,
    headroomMonthsEstimate,
  };
}

function generateProjection(
  offices: OfficeMapping[],
  selectedOffice: string | undefined,
): ProductivityProjection {
  const isAll = !selectedOffice || selectedOffice === "all";
  const office = offices.find((o) => o.officeName === selectedOffice);
  const officeIsLive = isAll ? offices.some((o) => o.liveOnAthena) : Boolean(office?.liveOnAthena);

  return {
    counterfactual: buildCounterfactual(officeIsLive),
    runway: buildRunway(officeIsLive),
  };
}

export async function fetchProductivityProjection(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean } = {},
): Promise<ProductivityProjection> {
  if (mockFor("/productivity-projection")) return mockResponse(generateProjection(offices, params.office));
  return apiGet<ProductivityProjection>("/productivity-projection", {
    range: params.range,
    office: params.office,
    compare: params.compare ? "true" : undefined,
  });
}

export function useProductivityProjection(
  params: { range?: string; office?: string; compare?: boolean } = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("productivity-projection", { ...params, offices: officesFingerprint(offices) }),
    queryFn: () => fetchProductivityProjection(offices ?? [], params),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}
